/**
 * Minimapa leve (Canvas 2D). North-up, centrado no player, mostrando a região ao redor.
 * Fog of War: células não exploradas ficam escuras. Símbolos próprios (não só cor):
 * player = seta, inimigo = ponto, elite = losango, boss = estrela, sanctuary = casa,
 * altar = triângulo, chest = quadrado, alpha den = caveira. Atualiza em baixa frequência.
 */
import { DIVIDE_MAP_COLOR, type DivideType } from "../world/Divides";
import { GameConfig } from "../config/GameConfig";
import type { FogOfWar } from "../world/FogOfWar";
import type { ViewportState } from "../core/ViewportManager";

export interface MinimapEntity {
  x: number;
  z: number;
  kind: "enemy" | "elite" | "boss" | "chest" | "room"; // room = sala da Fenda dos Titãs (10.9AS)
}
export interface MinimapData {
  px: number;
  pz: number;
  facing: number;
  entities: MinimapEntity[];
  /** 10.9EK — portas entre territorios (verde = aberta, vermelha = trancada). */
  gates?: { x: number; z: number; alongX: boolean; half: number; open: boolean }[];
  /** 10.9HR — piso da REGIÃO (css) pra célula revelada que não cai em território nenhum (rede de segurança). */
  regionGround?: string;
  /** 10.9HN — divisas naturais (cor por tipo) */
  divides?: { x0: number; z0: number; x1: number; z1: number; type: string }[];
  discovered: { sanctuary: boolean; altar: boolean; alphaDen: boolean };
  sanctuary: { x: number; z: number };
  sanctuaryRadius?: number; // 10.5J: raio da barreira/safe zone (anel no mapa)
  altar: { x: number; z: number };
  alphaDen: { x: number; z: number };
  gateZ: number;
  gateOpen: boolean;
  /** 10.5M/10.6X/10.7I — territórios como NÓS de trilha; `discovered` esconde os não visitados. */
  territories?: { cx: number; cz: number; halfW: number; halfH: number; tier: number; current: boolean; discovered?: boolean; col: number; row: number; ground?: string }[];
  terrName?: string;
  /** 10.9HX — dentro da Fenda dos Titãs: sem névoa (a zona fica fora do grid das regiões). */
  bossZone?: boolean;
  forest?: boolean; // true = Região 1 (mostra altar/alphaDen/gate)
  rift?: { x: number; z: number };
  riftDiscovered?: boolean;
}

const COL = {
  bgOut: "#0a0d16",
  fog: "#050710", // 10.6X — quase preto: unexplored SUME totalmente (era só escurinho)
  cloud1: "#0d1120", // manchas de nuvem sobre o fog
  cloud2: "#171d33",
  revealed: "#3a4a78", // explorado (mais claro) — 10.9HR: era #2a3555, quase igual à névoa
  zoneFloor: "#2a1c46", // 10.9HX — piso da Fenda dos Titãs (sem névoa: a zona é toda conhecida)
  border: "rgba(157,123,255,0.9)",
  player: "#ffd54a",
  enemy: "#ff6b6f",
  elite: "#ff5a8a",
  boss: "#ff3b6b",
  sanctuary: "#7c4dff",
  altar: "#4cc3ff",
  chest: "#ffb020",
  gate: "#9d7bff",
};

export class Minimap {
  private readonly root: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly label: HTMLDivElement;
  private readonly ctx: CanvasRenderingContext2D;
  private size = 120; // CSS px
  private readonly range = GameConfig.minimap.rangeWorld;

  constructor(uiRoot: HTMLElement, private fog: FogOfWar, onOpenWorld?: () => void) {
    this.root = document.createElement("div");
    this.root.className = "minimap";
    this.root.hidden = true;
    this.root.setAttribute("role", "button");
    this.root.setAttribute("aria-label", "Abrir mapa de regiões");
    this.root.title = "Toque para ver as regiões";
    this.root.tabIndex = 0;
    this.root.addEventListener("click", () => onOpenWorld?.());
    this.root.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenWorld?.(); }
    });
    this.canvas = document.createElement("canvas");
    this.root.appendChild(this.canvas);
    // 10.5M — nome do TERRITÓRIO atual logo abaixo do minimapa
    this.label = document.createElement("div");
    this.label.className = "minimap-terr";
    this.root.appendChild(this.label);
    uiRoot.appendChild(this.root);
    this.ctx = this.canvas.getContext("2d")!;
  }

  show(): void { this.root.hidden = false; }
  hide(): void { this.root.hidden = true; }

  /** Troca o buffer de fog (por região). */
  setFog(fog: FogOfWar): void {
    this.fog = fog;
  }

  /** Tamanho adaptativo pelo viewport (não cobre demais em telas pequenas). */
  setViewport(s: ViewportState): void {
    const base = Math.min(s.width, s.height);
    // Fase 10 (§9): minimapa CLARAMENTE menor (era até 150; agora compacto).
    this.size = Math.round(Math.min(108, Math.max(72, base * 0.23)));
    document.documentElement.style.setProperty("--minimap-size", `${this.size}px`);
    const dpr = Math.min(s.dpr, 2);
    this.canvas.width = Math.round(this.size * dpr);
    this.canvas.height = Math.round(this.size * dpr);
    this.canvas.style.width = `${this.size}px`;
    this.canvas.style.height = `${this.size}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  // 10.9HL — cor do piso por célula do fog (recalculada quando a lista de territórios muda = região nova)
  private cellColorKey: unknown = null;
  private cellColorCache: string[] | null = null;
  private cellColors(d: MinimapData): string[] | null {
    if (!d.territories) return null;
    // a lista chega nova a cada quadro (filtrada por alcance): a chave é a ASSINATURA dela, não a identidade
    const key = d.territories.map((t) => `${t.cx},${t.cz},${t.ground ?? ""}`).join("|") + "#" + (d.regionGround ?? "");
    if (this.cellColorKey === key && this.cellColorCache) return this.cellColorCache;
    const out = new Array<string>(this.fog.rows * this.fog.cols).fill(d.regionGround ? d.regionGround + "b0" : COL.revealed); // 10.9HR
    const c = { x: 0, z: 0 };
    for (let r = 0; r < this.fog.rows; r++) for (let col = 0; col < this.fog.cols; col++) {
      this.fog.cellCenter(col, r, c);
      const t = d.territories.find((t) => Math.abs(c.x - t.cx) <= t.halfW && Math.abs(c.z - t.cz) <= t.halfH);
      if (t?.ground) out[r * this.fog.cols + col] = t.ground + "b0"; // 69% de alfa: o fundo escuro ainda dá contraste
    }
    this.cellColorKey = key;
    this.cellColorCache = out;
    return out;
  }

  update(d: MinimapData): void {
    const ctx = this.ctx;
    const S = this.size;
    const cx = S / 2;
    const cy = S / 2;
    const scale = S / 2 / this.range;
    const w2mx = (wx: number): number => cx + (wx - d.px) * scale;
    const w2my = (wz: number): number => cy + (wz - d.pz) * scale;

    ctx.clearRect(0, 0, S, S);
    // clip circular
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, S / 2 - 1, 0, Math.PI * 2);
    ctx.clip();

    const cell = GameConfig.fog.cellSize;
    const reach = this.range + cell; // usado pelas divisas/portas também: fica fora do bloco da névoa
    // 10.9HX — FENDA DOS TITÃS: a zona fica FORA do mapa das regiões (z≈420), então nenhuma célula
    // dela existe no grid de névoa — o minimapa ficava 100% preto (Thiago: "o mapa de titãs não
    // aparece no mini mapa"). Lá não há exploração: desenha o piso da fenda direto, sem névoa.
    if (d.bossZone) {
      ctx.fillStyle = COL.zoneFloor;
      ctx.fillRect(0, 0, S, S);
    } else {
    // fundo (não explorado) — 10.6X: nuvens densas escondem tudo
    ctx.fillStyle = COL.fog;
    ctx.fillRect(0, 0, S, S);
    // manchas de "nuvem" pseudo-aleatórias (determinístico por posição de célula)
    ctx.fillStyle = COL.cloud1;
    for (let i = 0; i < 6; i++) {
      const angle = i * 1.037 + d.px * 0.0011 + d.pz * 0.0013;
      const rx = cx + Math.cos(angle) * (S * 0.28);
      const ry = cy + Math.sin(angle * 1.6) * (S * 0.28);
      ctx.beginPath(); ctx.arc(rx, ry, S * 0.16, 0, Math.PI * 2); ctx.fill();
    }
    ctx.fillStyle = COL.cloud2;
    for (let i = 0; i < 4; i++) {
      const angle = i * 1.53 + d.px * 0.0007 + d.pz * 0.0009;
      const rx = cx + Math.cos(angle * 1.3) * (S * 0.22);
      const ry = cy + Math.sin(angle) * (S * 0.22);
      ctx.beginPath(); ctx.arc(rx, ry, S * 0.10, 0, Math.PI * 2); ctx.fill();
    }

    // células reveladas dentro do alcance
    const cellPx = cell * scale;
    const _c = { x: 0, z: 0 };
    // 10.9HL — cada célula revelada tem a cor do PISO do bioma dela (cache por região)
    const colors = this.cellColors(d);
    ctx.fillStyle = COL.revealed;
    for (let r = 0; r < this.fog.rows; r++) {
      for (let c = 0; c < this.fog.cols; c++) {
        if (!this.fog.isRevealedCell(c, r)) continue;
        this.fog.cellCenter(c, r, _c);
        if (Math.abs(_c.x - d.px) > reach || Math.abs(_c.z - d.pz) > reach) continue;
        ctx.fillStyle = colors ? colors[r * this.fog.cols + c] : COL.revealed;
        ctx.fillRect(w2mx(_c.x) - cellPx / 2 - 0.5, w2my(_c.z) - cellPx / 2 - 0.5, cellPx + 1, cellPx + 1);
      }
    }
    } // fim do bloco SEM Fenda (fundo + névoa + células)
    // 10.9HN — DIVISAS (rio/lava/gelo/abismo) reveladas, na cor do tipo
    if (d.divides) {
      ctx.lineCap = "round";
      ctx.lineWidth = Math.max(1.5, 2.6 * scale);
      for (const dv of d.divides) {
        const mx = (dv.x0 + dv.x1) / 2, mz = (dv.z0 + dv.z1) / 2;
        if (Math.abs(mx - d.px) > reach + 12 || Math.abs(mz - d.pz) > reach + 12) continue;
        if (!this.fog.isRevealedWorld(mx, mz) && !this.fog.isRevealedWorld(dv.x0, dv.z0) && !this.fog.isRevealedWorld(dv.x1, dv.z1)) continue;
        ctx.strokeStyle = DIVIDE_MAP_COLOR[dv.type as DivideType] ?? "#8aa";
        ctx.globalAlpha = 0.8;
        ctx.beginPath(); ctx.moveTo(w2mx(dv.x0), w2my(dv.z0)); ctx.lineTo(w2mx(dv.x1), w2my(dv.z1)); ctx.stroke();
      }
      ctx.globalAlpha = 1;
    } else if (d.territories) {
      ctx.strokeStyle = "rgba(210, 215, 240, 0.55)";
      ctx.lineWidth = 1;
      for (const t of d.territories) {
        if (!t.discovered) continue;
        const x0 = w2mx(t.cx - t.halfW), y0 = w2my(t.cz - t.halfH), x1 = w2mx(t.cx + t.halfW), y1 = w2my(t.cz + t.halfH);
        if (x1 < -4 || y1 < -4 || x0 > S + 4 || y0 > S + 4) continue;
        ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
      }
    }

    // 10.7U — REMOVIDO: nós dourados de território + trilhas (a "bola amarrada" que confundia).
    // O minimapa agora mostra só o essencial de caça: base, inimigos, elites, boss e baús.
    // O nome do território continua no rótulo abaixo do minimapa.
    if (this.label.textContent !== (d.terrName ?? "")) this.label.textContent = d.terrName ?? "";

    const forest = d.forest !== false;
    // linha do gate (barreira) se revelada — só na Região 1
    if (forest && this.fog.isRevealedWorld(0, d.gateZ)) {
      ctx.strokeStyle = d.gateOpen ? "rgba(120,220,150,0.6)" : COL.gate;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, w2my(d.gateZ));
      ctx.lineTo(S, w2my(d.gateZ));
      ctx.stroke();
    }

    // 10.5J: BARREIRA de energia (anel da safe zone) visível no mapa
    if (d.sanctuaryRadius) {
      ctx.strokeStyle = "rgba(157,123,255,0.9)";
      ctx.lineWidth = 1.6;
      ctx.setLineDash([3, 2.5]);
      ctx.beginPath();
      ctx.arc(w2mx(d.sanctuary.x), w2my(d.sanctuary.z), d.sanctuaryRadius * scale, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    // POIs descobertos
    if (d.discovered.sanctuary) this.drawSanctuary(w2mx(d.sanctuary.x), w2my(d.sanctuary.z));
    if (forest && d.discovered.altar) this.drawAltar(w2mx(d.altar.x), w2my(d.altar.z));
    if (forest && d.discovered.alphaDen) this.drawAlphaDen(w2mx(d.alphaDen.x), w2my(d.alphaDen.z));
    // Rift Gate (Região 2 ou Região 1 pós-boss)
    if (d.rift && d.riftDiscovered) this.diamond(w2mx(d.rift.x), w2my(d.rift.z), 5, "#62e8ff");

    // 10.9EK — PORTAS entre territorios. O jogador so descobria onde era a passagem batendo no
    // muro. Agora a porta aparece como um traco no vao: VERDE aberta, VERMELHA trancada (com um
    // ponto no meio). Aparece se QUALQUER lado dela ja foi revelado (voce ve a saida do pedaco
    // em que esta, mesmo sem conhecer o outro lado).
    if (d.gates) {
      for (const g of d.gates) {
        const mx = w2mx(g.x);
        const my = w2my(g.z);
        if (mx < -8 || mx > S + 8 || my < -8 || my > S + 8) continue;
        const o = 4;
        const rev = g.alongX
          ? this.fog.isRevealedWorld(g.x - o, g.z) || this.fog.isRevealedWorld(g.x + o, g.z)
          : this.fog.isRevealedWorld(g.x, g.z - o) || this.fog.isRevealedWorld(g.x, g.z + o);
        if (!rev) continue;
        const halfPx = Math.max(3, g.half * scale);
        const col = g.open ? "#5ff0a0" : "#ff5a6a";
        ctx.strokeStyle = "rgba(0,0,0,0.75)";
        ctx.lineWidth = 5;
        ctx.lineCap = "round";
        ctx.beginPath();
        if (g.alongX) { ctx.moveTo(mx, my - halfPx); ctx.lineTo(mx, my + halfPx); } // vao corre em Z
        else { ctx.moveTo(mx - halfPx, my); ctx.lineTo(mx + halfPx, my); }        // vao corre em X
        ctx.stroke();
        ctx.strokeStyle = col;
        ctx.lineWidth = 3;
        ctx.stroke();
        if (!g.open) this.dot(mx, my, 2.2, "#ffe0e4"); // "cadeado": ponto claro no meio da porta trancada
      }
    }

    // entidades (dentro do alcance) — 10.6X: SÓ em células REVELADAS (nuvem esconde as outras)
    for (const e of d.entities) {
      const mx = w2mx(e.x);
      const my = w2my(e.z);
      if (mx < 0 || mx > S || my < 0 || my > S) continue;
      if (!d.bossZone && !this.fog.isRevealedWorld(e.x, e.z)) continue; // 10.9HX — na Fenda não há névoa
      if (e.kind === "enemy") this.dot(mx, my, 2, COL.enemy);
      else if (e.kind === "elite") this.diamond(mx, my, 4, COL.elite);
      else if (e.kind === "boss") this.star(mx, my, 6, COL.boss);
      else if (e.kind === "chest") this.square(mx, my, 4, COL.chest);
      else if (e.kind === "room") this.square(mx, my, 6, COL.boss); // 10.9AS — quadrados dos bosses na Fenda
    }

    // player (seta) no centro, orientado
    this.drawPlayer(cx, cy, d.facing);

    ctx.restore();
    // borda
    ctx.strokeStyle = COL.border;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, S / 2 - 1, 0, Math.PI * 2);
    ctx.stroke();
  }

  private dot(x: number, y: number, r: number, color: string): void {
    this.ctx.fillStyle = color;
    this.ctx.beginPath();
    this.ctx.arc(x, y, r, 0, Math.PI * 2);
    this.ctx.fill();
  }
  private diamond(x: number, y: number, r: number, color: string): void {
    const c = this.ctx;
    c.fillStyle = color;
    c.beginPath();
    c.moveTo(x, y - r); c.lineTo(x + r, y); c.lineTo(x, y + r); c.lineTo(x - r, y);
    c.closePath(); c.fill();
  }
  private square(x: number, y: number, r: number, color: string): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  private star(x: number, y: number, r: number, color: string): void {
    const c = this.ctx;
    c.fillStyle = color;
    c.beginPath();
    for (let i = 0; i < 10; i++) {
      const rad = i % 2 === 0 ? r : r * 0.45;
      const a = (Math.PI / 5) * i - Math.PI / 2;
      const px = x + Math.cos(a) * rad;
      const py = y + Math.sin(a) * rad;
      i === 0 ? c.moveTo(px, py) : c.lineTo(px, py);
    }
    c.closePath();
    c.fill();
  }
  private drawPlayer(x: number, y: number, facing: number): void {
    // direção de mundo (sin,cos) mapeada: +z é para BAIXO no minimapa
    const dx = Math.sin(facing);
    const dy = Math.cos(facing);
    const c = this.ctx;
    const len = 7;
    const wid = 4.5;
    const tx = x + dx * len;
    const ty = y + dy * len;
    const px = -dy;
    const py = dx; // perpendicular
    c.fillStyle = COL.player;
    c.beginPath();
    c.moveTo(tx, ty);
    c.lineTo(x + px * wid - dx * 2, y + py * wid - dy * 2);
    c.lineTo(x - px * wid - dx * 2, y - py * wid - dy * 2);
    c.closePath();
    c.fill();
  }
  private drawSanctuary(x: number, y: number): void {
    const c = this.ctx;
    c.fillStyle = COL.sanctuary;
    c.beginPath(); // casa: quadrado + telhado
    c.moveTo(x, y - 6); c.lineTo(x + 5, y - 1); c.lineTo(x - 5, y - 1); c.closePath(); c.fill();
    c.fillRect(x - 3.5, y - 1, 7, 6);
  }
  private drawAltar(x: number, y: number): void {
    this.diamond(x, y, 5, COL.altar);
    this.ctx.fillStyle = "#fff";
    this.ctx.fillRect(x - 1, y - 1, 2, 2);
  }
  private drawAlphaDen(x: number, y: number): void {
    // "caveira": círculo + dois olhos
    const c = this.ctx;
    c.fillStyle = COL.boss;
    c.beginPath();
    c.arc(x, y, 6, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = "#0a0d16";
    c.beginPath(); c.arc(x - 2, y - 1, 1.4, 0, Math.PI * 2); c.fill();
    c.beginPath(); c.arc(x + 2, y - 1, 1.4, 0, Math.PI * 2); c.fill();
    c.fillRect(x - 2, y + 2, 4, 2);
  }
}
